import { useEffect, useState } from 'react';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { CuffaMeasurementUnitEnum, CuffaProductTypeEnum, CuffaProductionTypeEnum } from 'common/enums';
import { formatCurrency } from 'utils/forms/cuffa_functions';
import type { FormikHelpers } from 'formik';

interface AvailabilityMonths {
  january?: boolean;
  february?: boolean;
  march?: boolean;
  april?: boolean;
  may?: boolean;
  june?: boolean;
  july?: boolean;
  august?: boolean;
  september?: boolean;
  october?: boolean;
  november?: boolean;
  december?: boolean;
  available_year_round?: boolean;
}

export interface ICuffaProduct {
  id?: string;
  uuid?: string;
  name: string;
  production_type: CuffaProductionTypeEnum;
  product_type: CuffaProductTypeEnum;
  is_processed: boolean;
  is_active: boolean;
  quantity?: number;
  unit?: CuffaMeasurementUnitEnum;
}

export default function useCuffaProductsData(options: {
  uuid?: string;
  productionTypes: CuffaProductionTypeEnum[];
  formik?: FormikHelpers<any> | null;
  reportError?: (message: string) => void;
}) {
  const { uuid, productionTypes, formik, reportError } = options;

  const [products, setProducts] = useState<ICuffaProduct[]>([]);
  const [productsById, setProductsById] = useState<any>({});
  const [dados, setDados] = useState<any>(null);

  useEffect(() => {
    let mounted = true;
    const fetchProducts = async () => {
      try {
        if (productionTypes && productionTypes.length > 0) {
          const response = await CuffaAPI.getProducts();
          if (mounted) {
            if (response.Status === 'OK' && Array.isArray(response.dados)) {
              setProducts(response.dados as ICuffaProduct[]);
            } else {
              setProducts([]);
            }
          }
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        if (reportError) reportError('Erro ao buscar produtos.');
        if (mounted) setProducts([]);
      }
    };

    fetchProducts();
    return () => { mounted = false; };
  }, [productionTypes]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      if (!uuid) {
        if (reportError) reportError('UUID da coleta não encontrado na URL.');
        return;
      }
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        if (mounted) {
          if (response && response.dados) {
            setDados(response.dados);
          } else {
            console.error('Dados de produção não encontrados na resposta da API:', response);
            setDados(null);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados de produção:', error);
        if (reportError) reportError('Erro ao buscar dados de produção.');
        if (mounted) setDados(null);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [uuid]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      if (!uuid) {
        if (reportError) reportError('UUID da coleta não encontrado na URL.');
        return;
      }
      try {
        const response = await CuffaAPI.getDataByIDProdutos(uuid);
        if (mounted) {
          if (response && response.dados) {
            setProductsById(response.dados);
          } else {
            console.error('Dados de produção não encontrados na resposta da API:', response);
            setProductsById({});
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados de produção:', error);
        if (reportError) reportError('Erro ao buscar dados de produção.');
        if (mounted) setProductsById({});
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [uuid]);

  // Populate form when dados and productsById are available
  useEffect(() => {
    if (!formik) return;

    try {
      if (dados?.uuid && Array.isArray(productsById) && productsById.length > 0) {
        const productionTypes = [...new Set(
          productsById
            .filter((product: any) => product.production_type)
            .map((product: any) => product.production_type)
        )].filter(Boolean) as CuffaProductionTypeEnum[];

        const managementType = productsById[0]?.management_type ?? '';

        const selectedProducts = productsById.map((product: any, index: number) => ({
          id: product.product_uuid ?? undefined,
          uuid: product.product_uuid ?? undefined,
          name: product.product_name ?? `Produto ${index + 1}`,
          production_type: product.production_type,
          product_type: product.product_type ?? CuffaProductTypeEnum.PADRAO,
          is_processed: Boolean(product.is_processed),
          is_active: true,
          quantity: Number(product.produced_quantity) ?? 1,
          unit: product.measurement_unit ?? CuffaMeasurementUnitEnum.QUILOGRAMAS,
        }));

        const firstProduct = productsById[0] ?? {};
        const availabilityMonths = firstProduct.availability_months ?? {};
        let hasCertification = null;

        if (firstProduct.has_certification !== undefined && firstProduct.has_certification !== null) {
          if (typeof firstProduct.has_certification === 'boolean') {
            hasCertification = firstProduct.has_certification;
          } else if (typeof firstProduct.has_certification === 'string') {
            hasCertification = firstProduct.has_certification.toLowerCase() === 'true';
          } else if (typeof firstProduct.has_certification === 'number') {
            hasCertification = firstProduct.has_certification === 1;
          }
        }

        formik.setValues({
          productionTypes,
          selectedProducts,
          managementType: managementType as any,
          area_hectares: firstProduct.area_hectares ? Number(firstProduct.area_hectares) : '',
          volume_produced: firstProduct.volume_produced ? Number(firstProduct.volume_produced) : '',
          volume_commercialized: firstProduct.volume_commercialized ? Number(firstProduct.volume_commercialized) : '',
          commercialized_value: firstProduct.commercialized_value
            ? formatCurrency((Number(firstProduct.commercialized_value) * 100).toString())
            : '',
          volume_consumed_donated: firstProduct.volume_consumed_donated ? Number(firstProduct.volume_consumed_donated) : '',
          volume_lost: firstProduct.volume_lost ? Number(firstProduct.volume_lost) : '',
          availability_months: {
            january: availabilityMonths.january ?? false,
            february: availabilityMonths.february ?? false,
            march: availabilityMonths.march ?? false,
            april: availabilityMonths.april ?? false,
            may: availabilityMonths.may ?? false,
            june: availabilityMonths.june ?? false,
            july: availabilityMonths.july ?? false,
            august: availabilityMonths.august ?? false,
            september: availabilityMonths.september ?? false,
            october: availabilityMonths.october ?? false,
            november: availabilityMonths.november ?? false,
            december: availabilityMonths.december ?? false,
            available_year_round: availabilityMonths.available_year_round ?? false,
          },
          has_certification: hasCertification,
          certification_type: firstProduct.certification_type || '',
          other_certification_name: firstProduct.other_certification_name || '',
        });
      } else if (dados?.uuid && (!productsById || Object.keys(productsById).length === 0)) {
        formik.resetForm();
      } else {
        formik.resetForm();
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Erro ao carregar dados de produtos:', error);
      }
    }
  }, [dados, productsById]);

  return {
    products,
    productsById,
    dados,
  };
}
